import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { MapPin, User as UserIcon, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { userSchema } from "../../schemavalidations";
import { CreateUserData, User } from "../../types";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import Button from "../UI/Button";
import { useAppSelector } from "../../hooks/useStore";

interface UserFormProps {
  user?: User;
  onSubmit: (data: CreateUserData | Partial<User>) => Promise<void>;
  loading: boolean;
  isEditMode?: boolean;
  isDriver?: boolean;
  roleOptions?: { value: string; label: string }[];
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  loading,
  isEditMode = false,
  isDriver = false,
  roleOptions,
}) => {
  const { t } = useTranslation("users");
  const [readonly, setReadonly] = useState(isEditMode);
  const { countries } = useAppSelector((state) => state.company);

  const handleUnlockFields = () => setReadonly(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateUserData>({
    resolver: yupResolver(userSchema) as any,
    context: { isCreateMode: !isEditMode },
  });

  const getCountryId = (
    country: string | { _id: string } | null | undefined,
  ): string => {
    if (!country) return "";
    if (typeof country === "object" && "_id" in country) return country._id;
    return country;
  };

  const getCountryName = (countryId: string): string => {
    const country = countries?.flat().find((c) => c._id === countryId);
    return country?.name || countryId || t("form.readonlyValues.notSpecified");
  };

  useEffect(() => {
    if (user && isEditMode) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone || "",
        role: user.role as CreateUserData["role"],
        address: {
          ...user.address,
          country: getCountryId(user.address?.country),
        },
      });
    }
    if (!isEditMode) {
      if (isDriver) {
        reset({ role: "driver" });
      } else if (roleOptions && roleOptions.length > 0) {
        reset({ role: roleOptions[0].value as CreateUserData["role"] });
      }
    }
  }, [user, isEditMode, isDriver, reset]);

  const onFormSubmit: (data: CreateUserData) => Promise<void> = (data) => {
    if (isEditMode) {
      // Backend rejects role updates with 400 — strip on edit.
      const { email, role, ...rest } = data;
      return onSubmit(rest);
    }
    return onSubmit(data);
  };

  const countryOptions = (countries ?? []).flat().map((country) => ({
    value: country._id,
    label: country.name,
  }));

  const roleSectionLabel = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : isDriver
      ? t("form.sections.information", { role: "Driver" })
      : t("form.sections.information", { role: "User" });

  if (isEditMode && readonly) {
    return (
      <div className="mx-auto bg-gray-50 rounded-md overflow-hidden">
        <div className="p-3 space-y-2">
          <SectionCard icon={UserIcon} title={roleSectionLabel}>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px]">
              <div className="flex gap-2">
                <dt className="w-24 text-gray-500 text-[12px]">
                  {t("form.fields.name")}
                </dt>
                <dd className="text-gray-900 font-medium">
                  {user?.firstName} {user?.lastName}
                  {user?.status && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800">
                      {user.status}
                    </span>
                  )}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-24 text-gray-500 text-[12px]">
                  {t("form.fields.email")}
                </dt>
                <dd className="text-gray-900 break-all">
                  {user?.email || t("form.readonlyValues.notSpecified")}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-24 text-gray-500 text-[12px]">
                  {t("form.fields.phone")}
                </dt>
                <dd className="text-gray-900">
                  {user?.phone || t("form.readonlyValues.notSpecified")}
                </dd>
              </div>
            </dl>
          </SectionCard>

          <SectionCard icon={MapPin} title={t("form.sections.address")}>
            <p className="text-[13px] text-gray-900">
              {[
                user?.address?.street,
                user?.address?.city,
                user?.address?.state,
                user?.address?.postalcode,
                user?.address?.country
                  ? getCountryName(getCountryId(user.address.country))
                  : null,
              ]
                .filter(Boolean)
                .join(", ") || t("form.readonlyValues.notSpecified")}
            </p>
          </SectionCard>
        </div>

        <div className="bg-white px-4 py-2.5 border-t border-gray-200 flex justify-end">
          <Button
            type="button"
            onClick={handleUnlockFields}
            variant="primary"
            icon={<Pencil className="w-3.5 h-3.5" />}
          >
            {t("form.buttons.edit")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <div className="p-3 space-y-2">
        <SectionCard icon={UserIcon} title={roleSectionLabel}>
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.firstName")}
              required
              error={errors.firstName?.message}
            >
              <Input
                {...register("firstName")}
                size="sm"
                error={!!errors.firstName}
                placeholder={t("form.placeholders.firstName")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.lastName")}
              required
              error={errors.lastName?.message}
            >
              <Input
                {...register("lastName")}
                size="sm"
                error={!!errors.lastName}
                placeholder={t("form.placeholders.lastName")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.email")}
              required
              error={errors.email?.message}
            >
              <Input
                {...register("email")}
                type="email"
                size="sm"
                error={!!errors.email}
                placeholder={t("form.placeholders.email")}
                readOnly={isEditMode}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.phone")}
              error={errors.phone?.message}
            >
              <Input
                {...register("phone")}
                type="tel"
                size="sm"
                error={!!errors.phone}
                placeholder={t("form.placeholders.phone")}
                readOnly={readonly}
              />
            </FormField>

            {!isEditMode && roleOptions && roleOptions.length > 0 && (
              <FormField
                layout="inline"
                size="sm"
                label={t("form.fields.role")}
                required
                error={errors.role?.message}
              >
                <Select
                  {...register("role")}
                  size="sm"
                  error={!!errors.role}
                  options={roleOptions}
                  disabled={readonly}
                  placeholder={t("form.placeholders.role")}
                />
              </FormField>
            )}
          </div>
        </SectionCard>

        <SectionCard icon={MapPin} title={t("form.sections.address")}>
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.street")}
              error={errors.address?.street?.message}
            >
              <Input
                {...register("address.street")}
                size="sm"
                error={!!errors.address?.street}
                placeholder={t("form.placeholders.street")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.city")}
              error={errors.address?.city?.message}
            >
              <Input
                {...register("address.city")}
                size="sm"
                error={!!errors.address?.city}
                placeholder={t("form.placeholders.city")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.state")}
              error={errors.address?.state?.message}
            >
              <Input
                {...register("address.state")}
                size="sm"
                error={!!errors.address?.state}
                placeholder={t("form.placeholders.state")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.zipcode")}
              error={errors.address?.zipcode?.message}
            >
              <Input
                {...register("address.zipcode")}
                size="sm"
                error={!!errors.address?.zipcode}
                placeholder={t("form.placeholders.zipcode")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.country")}
              error={errors.address?.country?.message}
            >
              <Select
                {...register("address.country")}
                size="sm"
                error={!!errors.address?.country}
                options={countryOptions}
                disabled={readonly}
                placeholder={t("form.placeholders.country")}
              />
            </FormField>
          </div>
        </SectionCard>
      </div>

      <div className="flex justify-end items-center gap-3 px-4 py-2.5 border-t border-gray-200 bg-white shrink-0">
        {isEditMode && readonly && (
          <Button
            type="button"
            onClick={handleUnlockFields}
            variant="outline"
            icon={<Pencil className="w-3.5 h-3.5" />}
          >
            {t("form.buttons.edit")}
          </Button>
        )}
        {(!isEditMode || (isEditMode && !readonly)) && (
          <Button
            type="submit"
            isLoading={loading}
            disabled={loading}
            variant="primary"
          >
            {isEditMode ? t("form.buttons.update") : t("form.buttons.create")}
          </Button>
        )}
      </div>
    </form>
  );
};
